import { BoxProps } from "@shopify/restyle";
import React, { useEffect, useState } from "react";

import {
  Box,
  HStack,
  Pressable,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { Theme } from "@whoosh/design-system/src/theme/restyle/theme";

import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronMiniFullDownIcon,
  ChevronMiniFullUpIcon,
} from "../../icons_v2/general";
import { AnimateHeight } from "./AnimateHeight";

export type AccordionCardProps = {
  Title: String | React.JSX.Element;
  InnerContent: React.JSX.Element;
  alwaysOpen?: boolean;
  isOpen?: boolean;
  secondaryItem?: React.JSX.Element;
  onChange?: (isOpen: boolean) => void;
  hideIcon?: boolean;
  bgColor?: BoxProps<Theme>["backgroundColor"];
  variant?: "light" | "dark";
  maxW?: BoxProps<Theme>["maxWidth"];
  hideVerticalPadding?: boolean;
  size?: "small" | "large";
  testID?: string;
  isDisabled?: boolean;
};

const AccordionCard = ({
  Title,
  InnerContent,
  alwaysOpen,
  isOpen,
  secondaryItem,
  onChange,
  hideIcon,
  bgColor,
  variant = "dark",
  maxW,
  hideVerticalPadding,
  size = "large",
  testID,
  isDisabled,
}: AccordionCardProps) => {
  const [open, setOpen] = useState(isOpen || false);

  useEffect(() => {
    if (onChange) onChange(open);
  }, [open, onChange]);

  useEffect(() => {
    if (isOpen !== null) setOpen(!!isOpen);
  }, [isOpen]);

  const handlePress = () => {
    if (!isDisabled) setOpen(!open);
  };

  const isClosed = !alwaysOpen && !open;

  const isHeading = typeof Title === "string";

  // TODO: Animate height is commented out until we have a fix in the next expo update.
  return (
    <Box
      backgroundColor={
        bgColor
          ? bgColor
          : variant === "dark"
          ? "surface-secondary"
          : "surface-primary"
      }
      borderRadius={size === "large" ? "4" : "2"}
      px={variant === "dark" ? (size === "small" ? 3 : 8) : 0}
      maxWidth={maxW || "100%"}
      py={hideVerticalPadding ? (size === "small" ? 2 : 0) : 8}
    >
      <Pressable
        onPress={handlePress}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        disabled={!!alwaysOpen}
        minHeight={variant === "dark" ? 24 : "auto"}
        testID={testID}
      >
        {isHeading ? (
          <Text
            variant={size === "large" ? "mainHeading" : undefined}
            maxFontSizeMultiplier={2}
            fontFamily={size === "small" ? "Inter_400Regular" : undefined}
            fontSize={size === "small" ? 12 : undefined}
            lineHeight={size === "small" ? 20 : undefined}
            letterSpacing={size === "small" ? 0 : undefined}
          >
            {Title}
          </Text>
        ) : (
          <>{Title}</>
        )}
        <HStack>
          {secondaryItem ? secondaryItem : null}
          {hideIcon ? null : (
            <Box
              width={32}
              height={32}
              pl={1}
              justifyContent="center"
              alignItems="center"
            >
              {!isClosed ? (
                size === "small" ? (
                  <ChevronMiniFullUpIcon />
                ) : (
                  <ChevronUpIcon />
                )
              ) : size === "small" ? (
                <ChevronMiniFullDownIcon />
              ) : (
                <ChevronDownIcon />
              )}
            </Box>
          )}
        </HStack>
      </Pressable>
      {alwaysOpen ? (
        <Box pt={size === "small" ? 4 : 6}>{InnerContent}</Box>
      ) : (
        <AnimateHeight
          enterFrom="bottom"
          hide={isClosed}
          children={InnerContent}
        />
      )}
    </Box>
  );
};

export default AccordionCard;
